<template>
    <div class="carousel" ref="carousel" v-if="newsList.length > 0">
        <div class="tips-box">
            <div class="tip-en">

                ART INFORMATION
            </div>
            <div class="tip-cn">
                艺术资讯
            </div>
        </div>
        <div class="slides-container" :style="{
            transform: `translateX(${translateX}px)`,
            transition: isTransitioning ? 'transform 0.3s ease' : 'none'
        }" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
            <!-- 修复：循环newsList，图片src绑定slide.imgUrl（原item.imgUrl错误） -->
            <div class="slide" v-for="(slide, index) in newsList" :key="index">
                <div class="slide-inner">
                    <div class="title-box">
                        <div class="time-box">
                            <div class="day">{{ slide.updateTime.split(' ')[0].split('-')[2] }}</div>

                            <div class="year">{{ slide.updateTime.split(' ')[0].split('-')[0] }}</div>
                            <span>/</span>
                            <div class="month">{{ slide.updateTime.split(' ')[0].split('-')[1] }}</div>
                        </div>
                        <div class="title-box">{{ slide.name }}</div>

                    </div>
                    <img :src="slide.imageUrl" :alt="slide.alt || `轮播图 ${index + 1}`" class="slide-img"
                        @error="handleImgError($event)">
                    <div class="slide-text" v-if="slide.text">{{ slide.text }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getNews } from '@/api/getNews'
export default {
    name: 'DynamicCarousel',
    // 新增：支持父组件自定义默认激活页（默认第二页，索引1）
    props: {
        defaultActiveIndex: {
            type: Number,
            default: 1
        }
    },
    data () {
        return {
            newsList: [], // 动态获取的轮播数据
            currentIndex: 0, // 当前激活页索引
            translateX: 0, // 轮播容器偏移量
            startX: 0, // 触摸起始X坐标
            moveX: 0, // 触摸移动距离
            isTransitioning: false, // 是否处于过渡动画中
            slideWidth: 0, // 单页宽度（容器的80%）
            gap: 12 // 页间距（左右各6px）
        }
    },
    async created () {
        // 加载远程数据
        try {
            this.newsList = await getNews()
            // 数据加载完成后，等待DOM渲染再初始化轮播
            // console.log(this.newsList)

            this.$nextTick(() => {
                this.initCarousel()
            })
        } catch (error) {
            console.error('轮播数据加载失败：', error)
            // 数据加载失败时，可添加兜底逻辑
            this.newsList = [
                { imgUrl: 'https://picsum.photos/id/1/600/800', text: '默认页1', alt: '兜底图1' },
                { imgUrl: 'https://picsum.photos/id/2/600/800', text: '默认页2', alt: '兜底图2' }
            ]
            this.$nextTick(() => this.initCarousel())
        }
    },
    mounted () {
        window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.handleResize)
    },
    // 监听newsList变化（如数据刷新），重新初始化
    watch: {
        newsList: {
            handler (newVal) {
                if (newVal.length > 0) {
                    this.$nextTick(() => this.initCarousel())
                }
            },
            deep: true
        }
    },
    methods: {
        // 初始化轮播（修复：原代码用slides，改为实际数据newsList）
        initCarousel () {
            const carousel = this.$refs.carousel
            if (!carousel || this.newsList.length === 0) return

            // 1. 验证并设置初始索引（防止默认索引超过数据长度）
            this.currentIndex = this.validateIndex(this.defaultActiveIndex)
            // 2. 计算轮播尺寸
            const carouselWidth = carousel.offsetWidth
            this.slideWidth = carouselWidth * 0.8 // 单页占容器80%，露出20%
            // 3. 计算初始位置（确保默认页居中）
            this.calculatePosition()
        },

        // 验证索引有效性（防止越界）
        validateIndex (index) {
            const maxIndex = this.newsList.length - 1
            return Math.max(0, Math.min(index, maxIndex))
        },

        // 核心：计算轮播容器偏移量（确保当前页居中）
        calculatePosition () {
            const carousel = this.$refs.carousel
            if (!carousel) return

            const carouselWidth = carousel.offsetWidth
            // 公式：偏移量 = -当前页偏移 + 容器左右留白的一半（确保当前页居中）
            this.translateX = -(this.currentIndex * (this.slideWidth + this.gap)) +
                (carouselWidth - this.slideWidth) / 2
        },

        // 窗口 resize 时重新计算
        handleResize () {
            this.$nextTick(() => {
                this.initCarousel()
            })
        },

        // 触摸开始
        onTouchStart (e) {
            this.isTransitioning = false // 拖动时关闭过渡动画
            this.startX = e.touches[0].clientX
        },

        // 触摸移动（修复：原代码用slides，改为newsList）
        onTouchMove (e) {
            const carousel = this.$refs.carousel
            if (!carousel || this.newsList.length <= 1) return

            // 计算实时移动距离
            this.moveX = e.touches[0].clientX - this.startX
            // 实时更新偏移量（带拖动效果）
            const carouselWidth = carousel.offsetWidth
            this.translateX = -(this.currentIndex * (this.slideWidth + this.gap)) +
                (carouselWidth - this.slideWidth) / 2 +
                this.moveX
        },

        // 触摸结束（修复：原代码用slides，改为newsList）
        onTouchEnd () {
            if (this.newsList.length <= 1) {
                this.moveX = 0
                return
            }

            this.isTransitioning = true // 结束拖动，开启过渡动画
            const threshold = this.slideWidth / 3 // 切换阈值（超过1/3页宽则切换）

            // 向右滑动：上一页
            if (this.moveX > threshold && this.currentIndex > 0) {
                this.currentIndex--
            }
            // 向左滑动：下一页
            else if (this.moveX < -threshold && this.currentIndex < this.newsList.length - 1) {
                this.currentIndex++
            }

            // 重置移动距离，更新最终位置
            this.moveX = 0
            this.calculatePosition()
            // 通知父组件当前页变化
            this.$emit('slide-change', this.currentIndex, this.newsList[this.currentIndex])
        },

        // 图片加载失败兜底
        handleImgError (e) {
            e.target.src = 'https://picsum.photos/id/99/600/800' // 兜底图片
        }
    }
}
</script>

<style scoped lang="less">
/* 样式保留原设计，优化细节 */
.carousel {
    width: 100%;
    height: 100vh;
    /* 可根据需求调整，如改为固定高度：300px */
    overflow: hidden;
    position: relative;
    max-width: 500px;
    /* 手机端最大宽度 */
    margin: 0 auto;
    padding-bottom: 100px;
    box-sizing: border-box;

    .tips-box {
        width: 100%;
        height: 120px;
        padding-top: 50px;
        box-sizing: border-box;

        .tip-en {
            width: 100%;

            font-size: 16px;
            text-align: center;
            color: #ABACB0;

        }

        .tip-cn {
            width: 100%;

            font-size: 28px;
            font-weight: bold;
            text-align: center;
            color: #FF7937;

        }
    }
}

.slides-container {
    display: flex;
    height: 100%;
    padding: 30px 0;
    box-sizing: border-box;
    will-change: transform;
    /* 优化动画性能 */
}

.slide {
    flex-shrink: 0;
    /* 禁止压缩，确保宽度固定 */
    width: 80%;
    /* 核心：单页占容器80%，相邻页露出20% */
    height: calc(100% - 60px);
    /* 减去上下内边距 */
    margin: 0 6px;
    /* 左右各6px间距，总gap=12px */
    position: relative;
}

.slide-inner {
    width: 100%;
    height: 100%;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

    .title-box {
        display: flex;
        width: 100%;
        height: 50px;
        box-sizing: border-box;
        align-items: center;

        .time-box {
            width: 100px;
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            border-right: 1px solid #000000;
            height: auto;

            .day {
                width: 100%;
                font-size: 24px;
                font-weight: bold;
                text-align: center;
            }

        }

        .title-box {
            width: calc(100% - 100px);
            height: auto;
            padding-left: 15px;
            box-sizing: border-box;
            font-size: 20px;
            font-weight: bold;
            display: -webkit-box;
            /* 旧版弹性盒子模型 */
            -webkit-box-orient: vertical;
            /* 文字垂直排列 */
            -webkit-line-clamp: 2;
            /* 限制显示的行数 */
            overflow: hidden;
            /* 隐藏溢出内容 */
            text-overflow: ellipsis;
            /* 添加省略号 */
            word-break: break-word;
            /* 允许单词内断行（可选） */
            box-sizing: border-box;

        }
    }
}

.slide-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* 保持图片比例，避免拉伸 */
}

.slide-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 32px;
    font-weight: bold;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);

    /* 适配小屏幕字体 */
    @media (max-width: 375px) {
        font-size: 24px;
    }
}
</style>